import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Loader, MoreVertical, PlusCircle, RefreshCcw, Pencil, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useModalState from "@/hooks/useModalState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import * as z from "zod";
import { toast } from "sonner";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const PAYMENT_TYPES = [
  "Bank",
  "Mobile Money",
  "Cash",
  "Card",
  "Cheque",
  "Other",
];

const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  type: z.string().default("Other"),
  provider: z.string().optional(),
  accountNumber: z.string().optional(),
  isActive: z.boolean().default(true),
});

function PaymentMethodForm({ isOpen, setIsOpen, refetch, record }: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  refetch: () => void;
  record: any;
}) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: record
      ? {
          name: record.name || "",
          description: record.description || "",
          type: record.type || "Other",
          provider: record.provider || "",
          accountNumber: record.accountNumber || "",
          isActive: record.isActive ?? true,
        }
      : {
          name: "",
          description: "",
          type: "Other",
          provider: "",
          accountNumber: "",
          isActive: true,
        },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    const q = record
      ? api.patch(`/payment-methods/${record.id}`, values)
      : api.post("/payment-methods", { ...values });
    return q
      .then(() => {
        refetch();
        toast.success(
          record
            ? "Payment method updated successfully"
            : "Payment method created successfully"
        );
        setIsOpen(false);
        form.reset();
      })
      .catch((e: any) => {
        toast.error(e.response?.data?.message || e.message);
        const errors = e?.response?.data?.meta?.errors || {};
        Object.keys(errors)?.forEach((field: any) => {
          form.setError(field, {
            message: errors[field],
          });
        });
      });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg p-0 gap-0">
        <DialogHeader className="border-b px-4 py-2.5">
          <DialogTitle className="text-[15px]">
            {record ? "Update Payment Method" : "Add New Payment Method"}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-full">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6 pt-3 pb-4 px-3"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Method Name *</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="e.g. Mobile Money"
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PAYMENT_TYPES.map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="provider"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Provider</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="e.g. MTN, Airtel, BNR"
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="accountNumber"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Account Number</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            placeholder="Account or phone number"
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="col-span-2">
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Brief description of this payment method"
                            error={fieldState?.error?.message}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </form>
          </Form>
        </ScrollArea>

        <DialogFooter className="border-t px-3 py-2.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              form.reset();
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={form.formState.disabled || form.formState.isSubmitting}
            type="submit"
            size="sm"
            onClick={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitting && (
              <Loader className="mr-2 h-4 w-4 text-white animate-spin" />
            )}
            {record ? "Update Method" : "Add Method"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentMethods({ embedded = false }: { embedded?: boolean }) {
  const [recordToEdit, setRecordToEdit] = useState<any>(undefined);
  const queryClient = useQueryClient();

  const toggleActiveMutation = useMutation(
    async (record: any) => {
      return api.patch(`/payment-methods/${record.id}`, {
        isActive: !record.isActive,
      });
    },
    {
      onSuccess: () => {
        toast.success("Payment method status updated");
        queryClient.invalidateQueries("payment-methods");
      },
      onError: (e: any) => {
        toast.error(e.response?.data?.message || e.message);
      },
    }
  );

  const columns: ColumnDef<any>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          ref={(el) => {
            if (el) el.indeterminate = table.getIsSomePageRowsSelected();
          }}
          checked={table.getIsAllPageRowsSelected()}
          onChange={(e) => table.toggleAllPageRowsSelected(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => (
        <div className="font-medium text-sm">{row.getValue("name")}</div>
      ),
    },
    {
      accessorKey: "provider",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Provider" />
      ),
      cell: ({ row }) => (
        <div className="text-sm text-gray-600">
          {row.getValue("provider") || "-"}
        </div>
      ),
    },
    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => {
        const type = row.getValue("type") as string;
        const colorMap: Record<string, string> = {
          Bank: "bg-blue-100 text-blue-800",
          "Mobile Money": "bg-green-100 text-green-800",
          Cash: "bg-emerald-100 text-emerald-800",
          Card: "bg-purple-100 text-purple-800",
          Cheque: "bg-orange-100 text-orange-800",
          Other: "bg-gray-100 text-gray-800",
        };
        return (
          <Badge className={`${colorMap[type] || colorMap.Other} text-xs font-medium`}>
            {type}
          </Badge>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const isActive = row.getValue("isActive") as boolean;
        return (
          <Badge
            className={`text-xs font-medium ${
              isActive
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "createdBy",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created By" />
      ),
      cell: ({ row }) => (
        <div className="text-sm text-gray-600">
          {row.getValue("createdBy") || "-"}
        </div>
      ),
    },
    {
      accessorKey: "updatedAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Updated At" />
      ),
      cell: ({ row }) => {
        const val = row.getValue("updatedAt") as string;
        return (
          <div className="text-sm text-gray-600">
            {val ? new Date(val).toLocaleDateString() : "-"}
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created At" />
      ),
      cell: ({ row }) => {
        const val = row.getValue("createdAt") as string;
        return (
          <div className="text-sm text-gray-600">
            {val ? new Date(val).toLocaleDateString() : "-"}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Actions" />
      ),
      cell: ({ row }) => {
        const record = row.original;
        return (
          <div className="flex items-center justify-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title="Edit"
              onClick={() => setRecordToEdit(record)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              title={record.isActive ? "Deactivate" : "Activate"}
              onClick={() => toggleActiveMutation.mutate(record)}
            >
              {record.isActive ? (
                <ToggleRight className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <ToggleLeft className="h-3.5 w-3.5 text-gray-400" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-500 hover:text-red-600"
              title="Delete"
              onClick={() => confirmModal.open({ meta: record })}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const newRecordModal = useModalState();
  const confirmModal = useConfirmModal();

  const recordsQuery = useQuery({
    queryKey: [
      "payment-methods",
      { search: searchText, filter: columnFilters, sort: sorting, pageIndex, pageSize },
    ],
    keepPreviousData: true,
    queryFn: async () => {
      const { data } = await api.get(`/payment-methods`, {
        params: {
          page_size: pageSize,
          page: pageIndex + 1,
          ...(searchText && { search: searchText }),
        },
      });
      return {
        items: data?.results || [],
        totalPages: data?.totalPages,
        total: data?.total,
      };
    },
  });

  const handleDelete = (record: any) => {
    confirmModal.setIsLoading(true);
    return api
      .delete(`/payment-methods/${record.id}`)
      .then(() => {
        recordsQuery.refetch();
        confirmModal.close();
        toast.success("Payment method deleted successfully");
      })
      .catch((e: any) => {
        confirmModal.setIsLoading(false);
        toast.error(e.message);
      });
  };

  const handleCreateDefaults = async () => {
    try {
      const { data } = await api.post("/payment-methods/defaults");
      toast.success(data?.message || "Default payment methods created");
      recordsQuery.refetch();
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  return (
    <>
      <ConfirmModal
        title="Are you sure you want to delete?"
        description="This will permanently delete the payment method and cannot be undone."
        meta={confirmModal.meta}
        onConfirm={(meta: any) => handleDelete(meta)}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      <div className={embedded ? "" : "px-2 py-3"}>
        {!embedded && (
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[16px] font-semibold tracking-tight">
                Payment Methods
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Manage payment methods used across the system
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCreateDefaults}
                className="text-xs"
              >
                <RefreshCcw size={14} className="mr-1.5" />
                Load Defaults
              </Button>
              <Button onClick={() => newRecordModal.open()} size="sm">
                <PlusCircle size={16} className="mr-1.5" />
                Add Method
              </Button>
            </div>
          </div>
        )}

        {embedded && (
          <div className="flex items-center justify-end gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCreateDefaults}
              className="text-xs"
            >
              <RefreshCcw size={14} className="mr-1.5" />
              Add Default Methods
            </Button>
            <Button onClick={() => newRecordModal.open()} size="sm">
              <PlusCircle size={16} className="mr-1.5" />
              Create New Method
            </Button>
          </div>
        )}

        <DataTable
          isFetching={recordsQuery.isFetching}
          defaultColumnVisibility={{ select: false }}
          isLoading={recordsQuery.status === "loading"}
          data={recordsQuery?.data?.items || []}
          columns={columns}
          onSearch={(e: string) => setSearchText(e)}
          sorting={sorting}
          setSorting={setSorting}
          pageCount={recordsQuery?.data?.totalPages}
          setPagination={setPagination}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setColumnFilters={setColumnFilters}
          columnFilters={columnFilters}
          facets={[]}
        />
      </div>

      <PaymentMethodForm
        isOpen={newRecordModal.isOpen || Boolean(recordToEdit)}
        setIsOpen={(e: boolean) => {
          newRecordModal.setisOpen(e);
          if (!e) setRecordToEdit(undefined);
        }}
        refetch={recordsQuery.refetch}
        record={recordToEdit}
      />
    </>
  );
}
